import { useState } from 'react';

import { Button, ButtonProps, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Icon } from '../Icon';
import { uiIcons } from '@/constants/uiIcons';
import {
  useConfirmDialog,
  type UseConfirmDialogOptions,
} from '@/hooks/useConfirmDialog';

interface DeleteButtonProps extends Omit<
  ButtonProps,
  'variant' | 'color' | 'children' | 'onClick' | 'loading'
> {
  deleteCallback: () => Promise<void>;
  deletionRedirectPath?: string;
  errorMessageSide?: 'left' | 'right';
  label?: string;
  confirmModalProps?: UseConfirmDialogOptions;
}

export const DeleteButton = ({
  deleteCallback,
  deletionRedirectPath,
  label,
  errorMessageSide = 'right',
  confirmModalProps,
  ...props
}: DeleteButtonProps) => {
  const { t } = useTranslation('ui');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  const { showConfirm } = useConfirmDialog();

  const handleClick = async () => {
    const { confirmed } = await showConfirm({
      variant: 'delete',
      ...confirmModalProps,
    });

    if (confirmed) {
      setError(false);
      try {
        setLoading(true);
        await deleteCallback();
        if (deletionRedirectPath) {
          navigate(deletionRedirectPath);
        }
      } catch (error) {
        // Handle error further if needed
        console.error('Delete action failed:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    }
  };
  return (
    <Stack
      direction={errorMessageSide === 'left' ? 'row-reverse' : 'row'}
      alignItems="center"
    >
      <Button
        startIcon={<Icon name={uiIcons.actions.delete} />}
        variant="outlined"
        color="error"
        loadingPosition="start"
        loading={loading}
        onClick={handleClick}
        aria-describedby={error ? 'deletion-error-message' : undefined}
        {...props}
      >
        {label ?? t('deleteButton.DefaultLabel')}
      </Button>
      {error && (
        <Typography
          variant="label"
          color="error"
          id="deletion-error-message"
          role="alert"
        >
          {t('deleteButton.DeletionErrorMessage')}
        </Typography>
      )}
    </Stack>
  );
};
